import {
  Box,
  Button,
  Center,
  HStack,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  MessageThreadSummaryQuery,
  MessageThreadSummaryQueryVariables,
  MessageThreadType,
} from "gql/graphql";
import { AiConversationIcon, AIIcon, CopyIcon } from "modules/icons";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import React, { memo, useCallback, useEffect, useState } from "react";
import colors from "app/colors";
import useBurstQuery from "modules/hooks/use-burst-query";
import useDisplayToast from "modules/hooks/use-display-toast";
import usePlainText from "modules/hooks/use-plain-text";
import { MIXPANEL_EVENT_TYPE } from "../../providers/mixpanel-provider/mixpanel-provider";
import Toast from "../../toasts/toast";
import summarySkeletonJson from "modules/layouts/animations/summary-skeleton.json";
import MESSAGE_THREAD_SUMMARY_QUERY from "../_networking/message-thread-summary";
import dynamic from "next/dynamic";
import MessagePlainText from "../messages/message-plain-text";

const Lottie = dynamic(() => import("react-lottie-player"), {
  ssr: false,
});

interface IProps {
  messageThreadId: string;
  type: MessageThreadType;
  isAtBottom: boolean;
}

const SummarizeConversationButton: React.FC<IProps> = ({
  messageThreadId,
  type,
  isAtBottom,
}) => {
  const { displayToast } = useDisplayToast();
  const { track } = useMixpanel();

  const [isHoveringSummarizeConversation, setIsHoveringSummarizeConversation] =
    useState(false);
  const [isMessageThreadSummaryOpen, setIsMessageThreadSummaryOpen] =
    useState(false);
  const [messageThreadSummaryHasError, setMessageThreadSummaryHasError] =
    useState(false);
  const [messageThreadSummaryText, setMessageThreadSummaryText] =
    useState<string>("");

  const isExpanded = isAtBottom || isHoveringSummarizeConversation;

  const [
    burstMessageThreadSummaryQuery,
    { loading: isMessageThreadSummaryLoading },
  ] = useBurstQuery<
    MessageThreadSummaryQuery,
    MessageThreadSummaryQueryVariables
  >(MESSAGE_THREAD_SUMMARY_QUERY);

  const onMessageThreadSummaryClicked = useCallback(async () => {
    if (isMessageThreadSummaryLoading || !messageThreadId) {
      return;
    }
    setIsMessageThreadSummaryOpen(true);
    setMessageThreadSummaryHasError(false);

    try {
      const { data } = await burstMessageThreadSummaryQuery({
        variables: { input: { messageThreadId } },
        fetchPolicy: "no-cache",
      });
      setMessageThreadSummaryText(data?.messageThreadSummary ?? "");
      track(MIXPANEL_EVENT_TYPE.SUMMARIZE_CONVERSATION);
    } catch (e) {
      setMessageThreadSummaryHasError(true);
    }
  }, [
    isMessageThreadSummaryLoading,
    messageThreadId,
    burstMessageThreadSummaryQuery,
    track,
  ]);

  // Close message summary popover when message thread changes
  useEffect(() => {
    setIsMessageThreadSummaryOpen(false);
  }, [messageThreadId]);

  const plainText = usePlainText();

  const onCopyClicked = useCallback(() => {
    const plainTextSummary = plainText.getPlainText(messageThreadSummaryText);
    if (!plainTextSummary) {
      displayToast(() => <Toast message="Nothing to Copy" />);
      return;
    }
    navigator.clipboard.writeText(plainTextSummary);
    displayToast(() => (
      <Toast
        message="Summary copied!"
        leftIcon={<CopyIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
  }, [messageThreadSummaryText, plainText, displayToast]);

  if (MessageThreadType.Chat === type) {
    return null;
  }

  return (
    <Popover
      placement="top-start"
      closeOnBlur={false}
      isOpen={isMessageThreadSummaryOpen}
    >
      <PopoverTrigger>
        <Button
          p="5px 9px 5px 5px"
          borderRadius="400px"
          bgGradient={colors.aiPurpleGradient.background}
          color="white"
          _hover={{
            bgGradient: colors.aiPurpleGradient.background,
          }}
          boxShadow="0px 8px 12px 6px rgba(0, 0, 0, 0.15), 0px 4px 4px 0px rgba(0, 0, 0, 0.14)"
          onMouseEnter={() => setIsHoveringSummarizeConversation(true)}
          onMouseLeave={() => setIsHoveringSummarizeConversation(false)}
          onClick={onMessageThreadSummaryClicked}
        >
          <AIIcon boxSize={5} fill={colors.white} />
          <Text
            pl={isExpanded ? "4px" : "0"}
            transition="max-width 0.3s ease-in-out"
            fontSize="13px"
            maxW={isExpanded ? "200px" : "0"} // animate width
            overflow="hidden"
          >
            Summarize
          </Text>
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          minW={450}
          boxShadow="xl"
          borderColor={colors.aiPurpleRight}
          borderWidth={1}
        >
          <PopoverCloseButton
            left={0}
            ml={2}
            mt={1.5}
            color={colors.gray30}
            onClick={() => setIsMessageThreadSummaryOpen(false)}
          />
          <PopoverHeader fontSize={18} fontWeight="bold" borderBottom="none">
            <Center>
              <HStack>
                <AiConversationIcon />
                <Text sx={colors.aiPurpleGradient}>Summary</Text>
              </HStack>
            </Center>
          </PopoverHeader>
          <PopoverBody fontSize={14} px={0}>
            {isMessageThreadSummaryLoading && (
              <VStack align="flex-start" px={2}>
                <Lottie play animationData={summarySkeletonJson} />
                <Text fontSize={12} sx={colors.aiPurpleGradient}>
                  Summary Generating...
                </Text>
              </VStack>
            )}
            {messageThreadSummaryHasError && (
              <Box px="40px" pb="16px">
                <Text fontSize={12} color={colors.red}>
                  Error Generating Summary
                </Text>
              </Box>
            )}
            {!isMessageThreadSummaryLoading &&
              !messageThreadSummaryHasError && (
                <VStack align="flex-start" pl={2}>
                  <Box maxH={300} overflowY="auto" color={colors.gray50}>
                    <MessagePlainText text={messageThreadSummaryText} />
                  </Box>
                  <Button variant="link" onClick={onCopyClicked} py="16px">
                    <HStack spacing={3}>
                      <CopyIcon boxSize={5} fill={colors.blue} />
                      <Text fontSize={12} color={colors.blue}>
                        Copy Summary
                      </Text>
                    </HStack>
                  </Button>
                </VStack>
              )}
          </PopoverBody>
          <Box
            h="10px"
            w="10px"
            bgColor={colors.white}
            sx={{
              transform: "rotate(-45deg)",
              boxShadow:
                "-2px 2px 3px 0px rgba(0, 0, 0, 0.25), 0px -1px 0px 0px #9747FF inset",
            }}
            position="absolute"
            bottom="-6px"
            right={isExpanded ? "80px" : "14px"}
            transition="all 0.3s ease-in-out"
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(SummarizeConversationButton);
